import { useState } from "react";
import styled from "styled-components";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import Lottie from "lottie-react";
import { useRecoilValue } from "recoil";
import { MdOutlineMovieCreation } from "react-icons/md";
import { HiOutlineInformationCircle } from "react-icons/hi";

import { Button, Link } from "MVVM/Components/Button";
import { TransitTime } from "MVVM/Components/TransitTime";
import { SignupToCreate } from "MVVM/Views/MatchHistory/Components/Shared/SignupToCreate";
import {
  ProcessingButton,
  TinyAvatar,
  TransitTimeWrapper,
} from "MVVM/Views/MatchHistory/styledComponents";
import { useIsMobileApp } from "MVVM/Hooks/useIsMobileApp";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { TMoment } from "MVVM/Views/MatchHistory/@types";
import {
  MatchHistoryGame,
  MatchHistoryWidgetSourceState,
  MatchHistoryWidgetIndentifierState,
} from "MVVM/State/matchHistory";
import { analytics } from "MVVM/Analytics";
import Remixing from "../../../../../../../animations/remixing.json";
import { StyledMomentIcon, TimePill } from "../styledComponents";
import { Card } from "../styledComponents";
import { MomentClip } from "../MomentClip";
import { MomentViewModel } from "./ViewModel";
import { MomentTags } from "../../MomentTags";
import { ESupportedGames, IUser } from "MVVM/@types";
import { getPlayerGameIdByGame } from "MVVM/Views/MatchHistory/utils";
import { CreatedForFriend } from "../../CreatedForFriend";
import { PlayerMenu } from "MVVM/Views/ClipExperience/FollowList";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { URLS } from "MVVM/Utilities/Maps";
import { PartnerViewSession } from "MVVM/State";
import { MatchHistoryLoginModal } from "../../LoginModal";
import {
  getMatchHistoryGameEnum,
  getPlayerSearchIdentifier,
} from "MVVM/Views/MatchHistory/utils";
import { useWindowSize } from "hooks";
import { ValorantComingSoonModal } from "./ValorantComingSoonModal";

const SUPPORT_URL = "https://help.allstar.gg/hc/en-us/requests/new";

interface IClipCreateError {
  message: string;
  support?: boolean;
}

const Moment = ({
  moment,
  isWidget,
  outdated,
  matchId,
}: {
  moment: TMoment;
  isWidget?: boolean;
  outdated?: boolean;
  matchId: string;
}) => {
  const { momentRequestClip } = MomentViewModel();
  const { allstarUser } = useUser();
  const { isMobileApp } = useIsMobileApp();
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const { width } = useWindowSize();
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <TransitTimeWrapper>
      <TransitTime playerGameId={moment?.player?.gameId} />
    </TransitTimeWrapper>,
  );
  const isOwnMoment =
    matchHistoryGame === ESupportedGames.VALORANT
      ? allstarUser?.user?.riot.riotId === moment.player.accountName
      : getPlayerGameIdByGame(matchHistoryGame, allstarUser) ===
        moment?.player?.gameId;

  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const widgetPlayerIdentifier = useRecoilValue(
    MatchHistoryWidgetIndentifierState,
  );
  const partnerViewSession = useRecoilValue(PartnerViewSession);
  const renderNewLoginModal = true;

  const useQuickEdit = matchHistoryGame !== ESupportedGames.FORTNITE;
  const {
    portalOpen: createdForFriendPortalOpen,
    setPortalOpen: setCreatedForFriendPortalOpen,
    Portal: CreatedForFriendPortal,
  } = usePortal(
    <CreatedForFriend
      game={matchHistoryGame}
      moment={moment}
      matchId={matchId}
    />,
  );
  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={moment.id}
      closeModal={() => setQuickEditPortalOpen(false)}
      onCreate={() => {
        if (!isOwnMoment) {
          setCreatedForFriendPortalOpen(true);
        }
        setProcessing(true);
      }}
    />,
    "root",
  );
  const {
    portalOpen: createValorantComingSoonPortalOpen,
    setPortalOpen: setValorantComingSoonPortalOpen,
    Portal: ValorantComingSoonPortal,
  } = usePortal(
    <ValorantComingSoonModal
      moment={moment}
      title="Valorant Clips are Coming Soon!"
      subtitle="We're adding Match History support for Valorant in the coming weeks. Check back here often for updates!"
      closeModal={() => {
        setValorantComingSoonPortalOpen(false);
      }}
    />,
  );
  const {
    portalOpen: signupPortalOpen,
    setPortalOpen: setSignupPortalOpen,
    Portal: SignupPortal,
  } = usePortal(<SignupToCreate />);
  const {
    portalOpen: matchHistoryLoginPortalOpen,
    setPortalOpen: setMatchHistoryLoginPortalOpen,
    Portal: MatchHistoryLoginPortal,
  } = usePortal(
    <MatchHistoryLoginModal
      game={getMatchHistoryGameEnum(moment.game) || matchHistoryGame}
      moment={moment}
      title="matchHistoryLoginModal.title"
      defaultTitle="Don't Miss Out On This Play!"
      subtitle="matchHistoryLoginModal.subtitle"
      defaultSubtitle="Create a FREE Allstar.gg account to create, edit, and share a clip from this moment."
    />,
  );
  const [error] = useState<IClipCreateError | null>(null);
  const [showPlayerData, setShowPlayerData] = useState("");
  const [processing, setProcessing] = useState(
    moment?.clip?.status === "PENDING",
  );

  const isPruned = moment?.clip?.status === "PRUNED";
  const isProcessed = moment?.clip?.status === "READY";
  const victimCount = moment?.victimData?.length;
  const firstTagValue = moment?.displayTags?.find((tag) => tag.key === "tag")
    ?.value;
  const hideTags = width && width < 700;

  const handleClick = async () => {
    if (widgetSource === "iframe") {
      analytics.track("Match History - Go to Match", {
        isWidget: true,
        widgetSource: widgetSource,
      });
      window.open(
        `${
          URLS.MATCH_HISTORY
        }?matchId=${matchId}&game=${matchHistoryGame}&playerSearchIdentifier=${widgetPlayerIdentifier}&partner=${partnerViewSession
          ?.clip?.partner?.partnerId
          ?.partnerId}&ajs_aid=${window.rudderanalytics.getAnonymousId()}`,
        "_blank",
      );
      return;
    }

    analytics.track("Match History - Request Clip", {
      userGroup: allstarUser?.user?.userGroup?.type,
      type: moment.type,
      tag: firstTagValue,
      isMobile: isMobileApp,
      game: matchHistoryGame,
      isWidget,
      isVisitor: !allstarUser.loggedIn,
      createForFriend: !isOwnMoment && allstarUser?.loggedIn,
      onPlatform: !!moment?.player?.user?._id,
    });

    if (!allstarUser.loggedIn) {
      if (renderNewLoginModal) return setMatchHistoryLoginPortalOpen(true);

      return setSignupPortalOpen(true);
    }

    if (!isOwnMoment) {
      analytics.track("Match History - Create For Friend", {
        type: "clip",
        game: matchHistoryGame,
        onPlatform: !!moment?.player?.user?._id,
      });
    }

    if (matchHistoryGame === ESupportedGames.VALORANT) {
      return setValorantComingSoonPortalOpen(true);
    }

    if (useQuickEdit) {
      setQuickEditPortalOpen(true);
    } else {
      setProcessing(true);
      await momentRequestClip({
        variables: {
          momentRequestClipInput: {
            id: moment.id,
          },
        },
      });
    }
  };

  return (
    <StyledCard>
      {createdForFriendPortalOpen && CreatedForFriendPortal}
      {portalOpen && Portal}
      {signupPortalOpen && SignupPortal}
      {quickEditPortalOpen && QuickEditPortal}
      {matchHistoryLoginPortalOpen && MatchHistoryLoginPortal}
      {createValorantComingSoonPortalOpen && ValorantComingSoonPortal}
      <Flex
        alignItems="center"
        style={{ gap: 24, position: "relative" }}
        flexWrap="wrap"
      >
        <Flex alignItems="center" style={{ gap: 6 }} flexShrink="0">
          <TinyAvatar src={moment?.player?.avatar} />
          {!!victimCount && (
            <Flex style={{ gap: 4 }} alignItems="center">
              <StyledMomentIcon
                src={moment.icons[0]}
                shouldFlipImage={matchHistoryGame === ESupportedGames.VALORANT}
              />
              <Victims>
                {moment?.victimData?.map((victim, index) => (
                  <Flex
                    onMouseEnter={() => {
                      if (widgetSource === "iframe") return;
                      setShowPlayerData(victim.gameId);
                    }}
                    onMouseLeave={() => setShowPlayerData("")}
                    key={`enemy-${moment.id}-${victim.gameId}-${index}`}
                    style={{ position: "relative" }}
                  >
                    <TinyAvatar key={index} src={victim.avatar} />
                    {showPlayerData === victim.gameId && (
                      <PlayerMenu
                        matchId={matchId}
                        player={{
                          accountName: victim.accountName,
                          gameId: victim.gameId,
                          displayName: victim.displayName,
                          avatar: victim.avatar,
                          user: victim.user as IUser,
                        }}
                        shouldShow={true}
                      />
                    )}
                  </Flex>
                ))}
              </Victims>
            </Flex>
          )}
        </Flex>
        <Flex
          style={{
            gap: 8,
            marginRight: "auto",

            alignItems: "start",
          }}
          flexDirection={"column"}
        >
          <Flex style={{ gap: 8 }} alignItems={"center"}>
            <Title title={moment.title} textAlign={"center"}>
              {error && (
                <ClipError>
                  <StyledInfo />
                  {error.message}
                  {error?.support && (
                    <SupportLink href={SUPPORT_URL}>
                      Contact support?
                    </SupportLink>
                  )}
                </ClipError>
              )}{" "}
              {moment.title}
            </Title>
            {moment.type === "secondary_fail" && (
              <FailIcon
                src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/matchHistory/fail.svg`}
              />
            )}
          </Flex>

          {!isProcessed && moment.type === "primary" && !hideTags && (
            <MomentTags
              tags={moment.displayTags.filter((tag) => tag.key === "tag")}
            />
          )}
        </Flex>
        <Box>
          {isProcessed || isPruned ? (
            <CreatedText>Clip created!</CreatedText>
          ) : processing ? (
            <StyledProcessingButton
              flex={true}
              size="tiny"
              onClick={() => setPortalOpen(true)}
            >
              Clip Pending
              <Lottie
                animationData={Remixing}
                loop={true}
                style={{ height: 36, width: 36, minWidth: 36 }}
              />
            </StyledProcessingButton>
          ) : !outdated ? (
            <ClipButton
              size="tiny"
              classification="roundedFilled"
              flex={true}
              disabled={error}
              onClick={async () => handleClick()}
            >
              <StyledMdOutlineMovieCreation />
              {isOwnMoment || !allstarUser.loggedIn
                ? "Clip"
                : "Clip for Friend"}
            </ClipButton>
          ) : (
            <></>
          )}
        </Box>
      </Flex>{" "}
      {(isProcessed || isPruned) && widgetSource !== "iframe" && (
        <MomentClip moment={moment} outdated={outdated} />
      )}
    </StyledCard>
  );
};
const FailIcon = styled(Image)`
  width: 50px;
`;

const StyledCard = styled(Card)`
  padding: 12px;
  flex-grow: 1;
  margin-bottom: 16px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    padding: 24px 12px;
  }
`;

const StyledProcessingButton = styled(ProcessingButton)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

const Victims = styled(Flex)`
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const StyledInfo = styled(HiOutlineInformationCircle)`
  color: ${({ theme }) => theme.colors.alert};
  height: 24px;
  width: 24px;
`;

const ClipError = styled(Flex)`
  align-items: center;
  bottom: -38px;
  color: ${({ theme }) => theme.colors.alert};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  gap: 4px;
  position: absolute;
  right: 0px;
`;

const Title = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[2]};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${(props) => props.theme.breaks.standard}) {
    font-size: ${({ theme }) => theme.fontSizes[2]};
  }
`;

const POTGPill = styled(TimePill)`
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSizes[1]};
  margin: 6px 0 6px 12px;
  padding: 2px 12px 2px 4px;

  @media (max-width: ${(props) => props.theme.breaks.standard}) {
    background: none;
    border: none;
    margin-left: 0;
    padding: 2px;
  }
`;

const CreatedText = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 500;
`;

const ClipButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 700;
  padding: 16px;

  :disabled {
    cursor: not-allowed;
  }

  :disabled svg {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;

const StyledMdOutlineMovieCreation = styled(MdOutlineMovieCreation)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  margin-right: 6px;
  min-width: 16px;
  width: 20px;
`;

const SupportLink = styled(Link)`
  color: ${({ theme }) => theme.colors.alert};
  text-decoration: underline;
`;

const StyledWeaponImage = styled(Image)`
  filter: brightness(0) saturate(100%) invert(80%) sepia(7%) saturate(717%)
    hue-rotate(144deg) brightness(82%) contrast(86%);
  flex: 1 1 24px;
  height: 14px;
  max-width: 100%;
`;

export { Moment };
